import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ErrorComponent } from "core/components/ErrorComponent";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { useCallback, useState } from "react";
import { DocNodeDocumentDisplay } from "./DocNodeDocumentDisplay";

function Wrapper({
  children,
  open,
  onClose,
}: {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog
      slotProps={{ paper: { sx: { height: "calc(100vh - 64px)" } } }}
      maxWidth="lg"
      fullWidth
      open={open}
      keepMounted={false}
      onClose={onClose}
    >
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
}

export function DmsDocumentViewModal({
  path,
  open,
  onClose,
  isEditMode,
}: {
  isEditMode: boolean;
  path: string;
  open: boolean;
  onClose: () => void;
}) {
  const locale = useLocale();
  const [selectedLocale, setSelectedLocale] = useState(locale);

  const apiPath = isEditMode
    ? ("/api/app/doc-node-manage/summary-with-parents" as const)
    : ("/api/app/doc-node/summary-with-parents" as const);

  const { data, isLoading, isError } = useCentrixApi({
    path: apiPath,
    parameters: { query: { locale: selectedLocale, path } },
    shouldFetch: open,
  });

  const handleClose = useCallback(() => {
    setSelectedLocale(locale);
    onClose();
  }, [onClose, locale]);

  if (isError) {
    return (
      <Wrapper open={open} onClose={handleClose}>
        <ErrorComponent apiError={isError} />
      </Wrapper>
    );
  }
  if (isLoading || !data) {
    return (
      <Wrapper open={open} onClose={handleClose}>
        <Stack
          direction="row"
          justifyContent="space-between"
          mb={1}
          alignItems="stretch"
        >
          <Skeleton variant="rounded">
            <Typography variant="h5" gutterBottom>
              Placeholder placeholder placeholder
            </Typography>
          </Skeleton>
          <Skeleton variant="rounded">
            <Button>Placeholder</Button>
          </Skeleton>
        </Stack>
        <Skeleton variant="text" width={200} />
        <Skeleton variant="text" width={200} />
        <Skeleton variant="rectangular" height="80vh" />
      </Wrapper>
    );
  }
  return (
    <Wrapper open={open} onClose={handleClose}>
      <DocNodeDocumentDisplay
        node={data}
        setSelectedLocale={setSelectedLocale}
      />
    </Wrapper>
  );
}
